/**
 * PostHog client bootstrap.
 *
 * The Emergent preview environment injects its own posthog-js via a CDN
 * script tag, which claims `window.posthog`. We use our own webpack-bundled
 * posthog-js module here — it is a SEPARATE module instance, so the two
 * don't collide at runtime. Our instance is exposed on `window.tgPosthog`
 * purely for dev-tools debugging; production code always goes through
 * phCapture() below.
 */

import posthog from "posthog-js";
import { getAnonUserId } from "@/lib/analytics";

let initialized = false;

function configuredKey() {
    return process.env.REACT_APP_POSTHOG_KEY;
}

export function initPostHog() {
    if (initialized) return posthog;
    if (typeof window === "undefined") return null;

    const key = configuredKey();
    const host =
        process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
    if (!key) return null;

    try {
        posthog.init(key, {
            api_host: host,
            // Privacy-first defaults (strict by design):
            autocapture: false,
            disable_session_recording: true,
            capture_pageview: true,
            capture_pageleave: true,
            persistence: "localStorage+cookie",
            person_profiles: "identified_only",
            loaded: (ph) => {
                try {
                    const uid = getAnonUserId();
                    if (uid) ph.identify(uid);
                } catch {
                    /* silent */
                }
            },
        });
        initialized = true;
        // Dev/debug handle so we can inspect OUR instance independently from
        // any platform-injected window.posthog.
        try {
            window.tgPosthog = posthog;
        } catch {
            /* silent */
        }
    } catch {
        /* silent — PostHog failures never break the app */
    }
    return posthog;
}

export function isPostHogReady() {
    return initialized;
}

export function phCapture(event, properties) {
    if (!initialized) return;
    try {
        posthog.capture(event, properties || {});
    } catch {
        /* silent */
    }
}

export function phDistinctId() {
    try {
        return posthog?.get_distinct_id?.() || null;
    } catch {
        return null;
    }
}

export default posthog;
